import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
} from "@mui/material";
import React from "react";
import BackBtn from "../../widgets/BackBtn";
import useApiEp from "../../hooks/useApiEp";
import { Link } from "react-router-dom";

const SubscriptionPlanStats = () => {
  const { data, isLoading, isError, error } = useApiEp(
    "/root/subscription-stats"
  );

  if (isLoading) {
    return (
      <Container
        maxWidth="md"
        sx={{ mt: 3, display: "flex", justifyContent: "center" }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container maxWidth="md" sx={{ mt: 3 }}>
        <Alert severity="error">
          Error: {error?.message || "Failed to fetch data"}
        </Alert>
      </Container>
    );
  }

  const { summary, planBreakdown } = data || { summary: {}, planBreakdown: [] };

  return (
    <Container maxWidth="md" sx={{ mt: 3, mb: 5 }}>
      <BackBtn />
      <Box sx={{ mt: 3 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
            mb: 3,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: "bold",
              color: "#1976d2",
              flexGrow: 1,
              fontSize: { xs: "1.75rem", sm: "2.125rem" },
            }}
          >
            Subscription Statistics
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Link
              to={`/home/subscription-accounts`}
              style={{
                textDecoration: "none",
                color: "#1976d2",
                fontWeight: "medium",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              View accounts list stats
            </Link>
          </Box>
        </Box>
        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            {
              label: "Total Accounts",
              value: summary.totalAccounts || 0,
              color: "#1976d2",
            },
            {
              label: "Active Accounts",
              value: summary.totalActive || 0,
              color: "#4caf50",
            },
            {
              label: "Expired Accounts",
              value: summary.totalExpired || 0,
              color: "#d32f2f",
            },
            {
              label: "Expiring Soon",
              value: summary.totalExpiringSoon || 0,
              color: "#ff9800",
            },
          ].map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item.label}>
              <Card
                sx={{
                  boxShadow: 3,
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": { transform: "translateY(-4px)", boxShadow: 6 },
                  borderLeft: `5px solid ${item.color}`,
                }}
              >
                <CardContent>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {item.label}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: "bold", color: item.color }}
                  >
                    {item.value}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Plan Breakdown Table */}
        <Typography
          variant="h5"
          component="h2"
          gutterBottom
          sx={{ mt: 4, fontWeight: "medium" }}
        >
          Plan Breakdown
        </Typography>
        <TableContainer component={Card} sx={{ boxShadow: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell sx={{ fontWeight: "bold" }}>
                  Subscription Plan
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">
                  Total Accounts
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">
                  Active
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">
                  Expired
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">
                  Expiring Soon
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {planBreakdown.map((plan, index) => (
                <TableRow
                  key={plan.subscriptionPlan || "no-plan"}
                  sx={{
                    "&:hover": { backgroundColor: "#e3f2fd" },
                    backgroundColor: index % 2 === 0 ? "#fafafa" : "white",
                  }}
                >
                  <TableCell>{plan.subscriptionPlan || "No Plan"}</TableCell>
                  <TableCell align="right">{plan.totalAccounts}</TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color:
                        plan.activeAccounts > 0 ? "#4caf50" : "text.secondary",
                    }}
                  >
                    {plan.activeAccounts}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color:
                        plan.expiredAccounts > 0 ? "#d32f2f" : "text.secondary",
                    }}
                  >
                    {plan.expiredAccounts}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color:
                        plan.expiringSoon > 0 ? "#ff9800" : "text.secondary",
                    }}
                  >
                    {plan.expiringSoon}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Container>
  );
};

export default SubscriptionPlanStats;
